<template>
  <div
    v-if="estimate"
    :class="['detailed-pdf-document', { 'english-document': isEnglish }]"
    data-estimate-pdf
    data-estimate-pdf-ready="true"
  >
    <section class="detailed-pdf-page cover-page" data-estimate-pdf-page>
      <DraftWatermark v-if="isDraft" />

      <h1 class="cover-title">{{ label('御 見 積 書', 'QUOTATION') }}</h1>

      <div class="cover-main">
        <div class="cover-recipient-area">
          <div class="recipient-block">
            <div class="recipient-name">{{ recipientName }}</div>
          </div>

          <table class="cover-condition-table">
            <tbody>
              <tr>
                <th>{{ label('工事名', 'Project Name') }}</th>
                <td>{{ estimateTitle }}</td>
              </tr>
              <tr>
                <th>{{ label('工事場所', 'Work Location') }}</th>
                <td>{{ workLocation }}</td>
              </tr>
              <tr>
                <th>{{ label('工期', 'Work Period') }}</th>
                <td>{{ deliveryTerms || '-' }}</td>
              </tr>
              <tr>
                <th>{{ label('有効期限', 'Valid Until') }}</th>
                <td>{{ estimate.valid_until ? formatDate(estimate.valid_until) : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="cover-issuer-area">
          <div>{{ formatDate(estimate.issue_date) }}</div>
          <div class="cover-estimate-number">
            {{ label('見積番号', 'Quotation No.') }} {{ estimate.estimate_no || '-' }}
            <span v-if="Number(estimate.revision_no) > 0">{{ formatRevisionLabel(estimate.revision_no) }}</span>
          </div>
          <div
            :class="[
              'cover-company-block',
              { 'cover-company-block--with-seal': Boolean(estimate.seal_flg) }
            ]"
          >
            <CompanyBlock :estimate="estimate" />
            <CompanySeal
              :visible="Boolean(estimate.seal_flg)"
              class="company-seal"
            />
          </div>
          <div class="cover-approval-stamp" role="group" aria-label="approval stamp">
            <span>{{ label('承認', 'Approved') }}</span>
            <span>{{ label('確認', 'Reviewed') }}</span>
            <span>{{ label('作成', 'Prepared') }}</span>
            <span>
              <EmployeeSeal
                :name="estimate.approved_stamp_name || estimate.approved_by_user?.name || ''"
                :image-url="estimate.approved_stamp_image_url || ''"
                :size="36"
                class="approval-employee-seal"
              />
            </span>
            <span></span>
            <span>
              <EmployeeSeal
                :name="estimate.created_stamp_name || estimate.created_by_user?.name || ''"
                :image-url="estimate.created_stamp_image_url || ''"
                :size="36"
                class="approval-employee-seal"
              />
            </span>
          </div>
        </div>
      </div>

      <div class="grand-total">
        <span>{{ label('御見積金額', 'Total Quotation Amount') }}</span>
        <strong>{{ currency(estimate.total_amount) }}</strong>
        <small>{{ label('（消費税込）', '(Tax included)') }}</small>
      </div>

      <table class="cover-amount-table">
        <tbody>
          <tr>
            <th>{{ label('工事費', 'Construction Cost') }}</th>
            <td>{{ number(netAmount) }}</td>
          </tr>
          <tr>
            <th>{{ label(formatEstimateTaxLabel(estimate.tax_rate), 'Consumption Tax') }}</th>
            <td>{{ number(estimate.tax_amount) }}</td>
          </tr>
          <tr class="cover-amount-total-row">
            <th>{{ label('合計', 'Total') }}</th>
            <td>{{ number(estimate.total_amount) }}</td>
          </tr>
        </tbody>
      </table>

      <div class="cover-notes">
        <div>{{ label('・本見積書は上記有効期限内に限り有効です。', 'This quotation is valid until the date stated above.') }}</div>
        <div>{{ label('・追加工事が生じた場合は別途ご相談させていただきます。', 'Additional work will be quoted separately.') }}</div>
      </div>
    </section>

    <section
      v-for="(page, pageIndex) in breakdownPages"
      :key="`breakdown-page-${pageIndex}`"
      class="detailed-pdf-page page-break"
      data-estimate-pdf-page
    >
      <DraftWatermark v-if="isDraft" />
      <PageHeader
        :title="label('工事別内訳書', 'PROJECT BREAKDOWN')"
      />

      <table class="breakdown-table">
        <thead>
          <tr>
            <th class="breakdown-no">{{ label('番号', 'No.') }}</th>
            <th>{{ label('工事項目', 'Work Item') }}</th>
            <th class="breakdown-quantity">{{ label('数量', 'Qty.') }}</th>
            <th class="breakdown-unit">{{ label('単位', 'Unit') }}</th>
            <th class="breakdown-amount">{{ label('金額', 'Amount') }}</th>
            <th class="breakdown-notes">{{ label('備考', 'Notes') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in page.rows" :key="row.key">
            <tr v-if="row.kind === 'group'">
              <td class="center-cell">{{ roman(row.index + 1) }}</td>
              <td class="multiline">{{ row.name }}</td>
              <td class="number-cell">{{ formatQuantity(row.quantity, row.quantity_decimal_places) }}</td>
              <td class="center-cell">{{ row.unit }}</td>
              <td class="number-cell">{{ number(row.amount) }}</td>
              <td class="multiline">{{ row.notes }}</td>
            </tr>
            <tr v-else-if="row.kind === 'adjustment'">
              <td></td><td class="multiline">{{ row.name }}</td><td></td><td></td><td class="number-cell">-{{ number(row.amount) }}</td><td class="multiline">{{ row.notes }}</td>
            </tr>
            <tr v-else-if="row.kind === 'note'" class="breakdown-note-row">
              <td colspan="6" class="multiline">※ {{ row.text }}</td>
            </tr>
            <template v-else-if="row.kind === 'blank'">
              <tr class="breakdown-blank-row"><td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td></tr>
            </template>
            <tr v-else class="breakdown-blank-row"><td colspan="6">&nbsp;</td></tr>
          </template>
          <template v-if="pageIndex === breakdownPages.length - 1">
            <tr class="breakdown-total-row">
              <td colspan="4">{{ label('工事費計', 'Construction Cost') }}</td>
              <td class="number-cell">{{ number(netAmount) }}</td>
              <td></td>
            </tr>
            <tr class="breakdown-tax-row">
              <td colspan="4">{{ label(formatEstimateTaxLabel(estimate.tax_rate), 'Consumption Tax') }}</td>
              <td class="number-cell">{{ number(estimate.tax_amount) }}</td>
              <td></td>
            </tr>
            <tr class="breakdown-total-row">
              <td colspan="4">{{ label('合計', 'Total') }}</td>
              <td class="number-cell">{{ number(estimate.total_amount) }}</td>
              <td></td>
            </tr>
          </template>
          <tr v-else class="breakdown-total-row">
            <td colspan="4">
              {{ label('次頁へ', 'Continued') }}
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
      <div v-if="breakdownPages.length > 1" class="page-footer">
        {{ pageIndex + 1 }} / {{ breakdownPages.length }}
      </div>
    </section>

    <section
      v-for="(page, pageIndex) in detailPages"
      :key="`detail-page-${pageIndex}`"
      class="detailed-pdf-page page-break"
      data-estimate-pdf-page
    >
      <DraftWatermark v-if="isDraft" />
      <PageHeader
        :title="label('内訳明細書', 'DETAILED BREAKDOWN')"
      />

      <table class="detail-table">
        <thead>
          <tr>
            <th class="detail-name">{{ label('名称', 'Description') }}</th>
            <th class="detail-part">{{ label('部位', 'Part') }}</th>
            <th class="detail-spec">{{ label('仕様', 'Specification') }}</th>
            <th class="detail-quantity">{{ label('数量', 'Qty.') }}</th>
            <th class="detail-unit">{{ label('単位', 'Unit') }}</th>
            <th class="detail-price">{{ label('単価', 'Unit Price') }}</th>
            <th class="detail-amount">{{ label('金額', 'Amount') }}</th>
            <th class="detail-notes">{{ label('備考', 'Notes') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in page" :key="row.key">
            <tr v-if="row.kind === 'section'" class="detail-section-row">
              <td colspan="8" class="multiline">{{ roman(row.groupIndex + 1) }}&emsp;{{ row.name }}</td>
            </tr>
            <tr v-else-if="row.kind === 'subtotal'" class="detail-subtotal-row">
              <td colspan="6">
                {{ roman(row.groupIndex + 1) }}&emsp;{{ label('小計', 'Subtotal') }}
              </td>
              <td class="number-cell">{{ number(row.amount) }}</td>
              <td></td>
            </tr>
            <tr v-else-if="row.kind === 'line' && row.line.line_type === 'note'" class="detail-note-row"><td colspan="8" class="multiline">※ {{ lineText(row.line) }}</td></tr>
            <tr v-else-if="row.kind === 'line'" class="estimate-detail-row">
              <td class="multiline">{{ lineName(row.line) }}</td>
              <td class="multiline">{{ linePart(row.line) }}</td>
              <td class="multiline">{{ lineSpecification(row.line) }}</td>
              <!-- 値引は金額だけを表示する -->
              <td class="number-cell">{{ row.line.line_type === 'discount' ? '' : formatQuantity(row.line.quantity, row.line.quantity_decimal_places) }}</td>
              <td class="center-cell">{{ row.line.line_type === 'discount' ? '' : lineUnit(row.line) }}</td>
              <td class="number-cell">{{ unitPrice(row.line) }}</td>
              <td class="number-cell">{{ lineAmount(row.line) }}</td>
              <td class="multiline">{{ lineNotes(row.line) }}</td>
            </tr>
            <template v-else-if="row.kind === 'blank'">
              <tr class="detail-blank-row"><td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>
            </template>
            <tr v-else class="detail-blank-row"><td colspan="8">&nbsp;</td></tr>
          </template>
        </tbody>
      </table>

      <div class="page-footer">{{ pageIndex + 1 }}</div>
    </section>
  </div>
</template>

<script setup>
import { computed, defineComponent, h } from 'vue';
import CompanySeal from '@/components/shared/CompanySeal.vue';
import EmployeeSeal from '@/components/shared/EmployeeSeal.vue';
import {
  formatEstimateQuantity,
  formatEstimateTaxLabel,
  isEnglishEstimateLanguage,
  LANDSCAPE_BREAKDOWN_ROWS_PER_PAGE,
  paginateRowsByWeight,
  wrappedTextLineCount
} from '@/utils/estimateEditor';
import { formatRevisionLabel } from '@/utils/estimates';
import {
  formatEnglishGregorianDate,
  formatJapaneseGregorianDate
} from '@/utils/estimatePdfDates';

const DETAIL_ROWS_PER_PAGE = 23;

const props = defineProps({
  estimate: {
    type: Object,
    required: true
  }
});

const DraftWatermark = defineComponent({
  name: 'EstimateDetailedDraftWatermark',
  setup() {
    return () => h('div', { class: 'draft-watermark' }, 'DRAFT');
  }
});

const CompanyBlock = defineComponent({
  name: 'EstimateDetailedCompanyBlock',
  props: {
    estimate: {
      type: Object,
      required: true
    }
  },
  setup(componentProps) {
    const english = componentProps.estimate.default_language_type === 'english';
    return () => h('div', { class: 'company-block' }, [
      h(
        'div',
        { class: 'company-name' },
        english ? 'Soei Planning Co., Ltd.' : '株式会社 創英企画'
      ),
      h(
        'div',
        english
          ? '2-38-6 Yoyogi, Shibuya-ku, Tokyo 151-0053, Japan'
          : '〒151-0053 東京都渋谷区代々木2-38-6'
      ),
      h('div', english
        ? 'Tel: 03-3370-8006 / Fax: 03-3370-8157'
        : 'TEL 03-3370-8006 / FAX 03-3370-8157')
    ]);
  }
});

const PageHeader = defineComponent({
  name: 'EstimateDetailedPageHeader',
  props: {
    title: { type: String, required: true }
  },
  setup(componentProps) {
    return () => h('div', { class: 'page-header' }, [h('h2', componentProps.title)]);
  }
});

const isEnglish = computed(() => isEnglishEstimateLanguage(props.estimate?.default_language_type));
const label = (japanese, english) => isEnglish.value ? english : japanese;

const isDraft = computed(() => props.estimate?.approval_status !== 'approved');
const netAmount = computed(() =>
  Math.max(
    Number(props.estimate?.subtotal_amount || 0) -
    Number(props.estimate?.discount_amount || 0),
    0
  )
);

const recipientName = computed(() => {
  const recipient = props.estimate?.recipient || {};
  if (isEnglish.value) {
    return [recipient.customer_nm, recipient.department_nm]
      .filter(Boolean)
      .join(', ');
  }
  return [recipient.customer_nm, recipient.department_nm, '御中']
    .filter(Boolean)
    .join(' ');
});

const estimateTitle = computed(() => props.estimate?.title || '');

const workLocation = computed(() =>
  props.estimate?.work_location ||
  [
    props.estimate?.building_snapshot?.building_nm,
    props.estimate?.building_snapshot?.address
  ].filter(Boolean).join(isEnglish.value ? ', ' : '\u3000') ||
  '-'
);

const generalNotes = computed(() => props.estimate?.general_notes || '');

const deliveryTerms = computed(() => props.estimate?.delivery_terms || '');

const sectionAmount = lines =>
  (lines || []).reduce((total, line) => {
    const amount = Number(line.amount || 0);
    if (line.line_type === 'detail') return total + amount;
    if (line.line_type === 'discount') return total - amount;
    return total;
  }, 0);

const sectionBreakdownAmount = lines =>
  (lines || []).reduce((total, line) =>
    line.line_type === 'detail' ? total + Number(line.amount || 0) : total,
  0);

const estimateGroups = computed(() => {
  return (props.estimate?.sections || []).map((section, index) => ({
    key: `section-${section.id}`,
    index,
    name: section.section_nm,
    notes: section.notes,
    quantity: section.quantity,
    quantity_decimal_places: section.quantity_decimal_places,
    unit: section.unit_nm || label('式', 'Lot'),
    lines: (section.lines || []).filter(line => line.line_type !== 'subtotal'),
    amount: sectionAmount(section.lines),
    breakdownAmount: sectionBreakdownAmount(section.lines)
  }));
});

const breakdownRows = computed(() => {
  const rows = [];
  estimateGroups.value.forEach(group => {
    rows.push({
      ...group,
      kind: 'group',
      amount: group.breakdownAmount
    });
    group.lines.forEach((line, index) => {
      if (line.line_type === 'discount') {
        rows.push({
          key: `${group.key}-adjustment-${line.id || index}`,
          kind: 'adjustment',
          name: lineName(line) || label('調整費', 'Adjustment'),
          notes: lineNotes(line),
          amount: Math.abs(Number(line.amount || 0))
        });
      } else if (line.line_type === 'note') {
        rows.push({
          key: `${group.key}-note-${line.id || index}`,
          kind: 'note',
          text: lineText(line)
        });
      }
    });
  });

  (props.estimate?.unsectioned_lines || []).forEach((line, index) => {
    if (line.line_type === 'subtotal') return;
    if (line.line_type === 'note') {
      rows.push({
        key: `unsectioned-note-${line.id || index}`,
        kind: 'note',
        text: lineText(line)
      });
      return;
    }
    rows.push({
      key: `unsectioned-${line.id || index}`,
      kind: line.line_type === 'discount' ? 'adjustment' : 'group',
      index: rows.filter(row => row.kind === 'group').length,
      name: lineName(line) || label('その他', 'Other'),
      notes: lineNotes(line),
      amount: Math.abs(Number(line.amount || 0)),
      quantity: line.quantity,
      quantity_decimal_places: line.quantity_decimal_places,
      unit: line.unit_nm
    });
  });

  if (generalNotes.value) {
    generalNotes.value.split(/\r?\n/).filter(Boolean).forEach((text, index) => {
      rows.push({ key: `general-note-${index}`, kind: 'note', text });
    });
  }
  return rows;
});

const breakdownRowWeight = row => {
  if (row.kind === 'blank') return 1;
  if (['group', 'adjustment'].includes(row.kind)) {
    return Math.max(
      wrappedTextLineCount(row.name, 55),
      wrappedTextLineCount(row.notes, 24)
    );
  }
  if (row.kind === 'note') return wrappedTextLineCount(row.text, 120);
  return 1;
};
const detailRowWeight = row => {
  if (row.kind === 'blank') return 2;
  if (row.kind === 'section') return wrappedTextLineCount(row.name, 120);
  if (row.kind === 'line' && row.line?.line_type === 'note') {
    return wrappedTextLineCount(lineText(row.line), 120);
  }
  if (row.kind === 'line') {
    const mainRows = Math.max(
      wrappedTextLineCount(lineName(row.line), 28),
      wrappedTextLineCount(linePart(row.line), 18),
      wrappedTextLineCount(lineSpecification(row.line), 28),
      wrappedTextLineCount(lineNotes(row.line), 18)
    );
    return mainRows;
  }
  return 1;
};
const breakdownPages = computed(() => {
  const summaryRows = 3;
  const sourcePages = paginateRowsByWeight(
    breakdownRows.value,
    LANDSCAPE_BREAKDOWN_ROWS_PER_PAGE,
    summaryRows,
    breakdownRowWeight
  );
  return sourcePages.map(sourceRows => ({ rows: sourceRows }));
});

const detailGroups = computed(() => {
  const groups = [...estimateGroups.value];
  const detailLines = (props.estimate?.unsectioned_lines || [])
    .filter(line => !['note', 'subtotal'].includes(line.line_type));
  if (detailLines.length) {
    groups.push({
      key: 'unsectioned-detail',
      index: groups.length,
      name: label('その他・調整', 'Other / Adjustment'),
      notes: '',
      lines: detailLines,
      amount: sectionAmount(detailLines)
    });
  }
  return groups;
});

const detailRows = computed(() => {
  const rows = [];
  detailGroups.value.forEach(group => {
    rows.push({
      key: `${group.key}-heading`,
      kind: 'section',
      groupIndex: group.index,
      name: group.name
    });
    group.lines.forEach((line, lineIndex) => {
      rows.push({
        key: `${group.key}-line-${line.id || lineIndex}`,
        kind: 'line',
        groupIndex: group.index,
        lineIndex,
        line
      });
    });
    rows.push({
      key: `${group.key}-subtotal`,
      kind: 'subtotal',
      groupIndex: group.index,
      name: group.name,
      amount: group.amount
    });
  });
  return rows;
});

const detailPages = computed(() => {
  return paginateRowsByWeight(
    detailRows.value,
    DETAIL_ROWS_PER_PAGE,
    0,
    detailRowWeight,
    { keepWithNext: row => row.kind === 'section' }
  )
    .map(sourceRows => sourceRows);
});

const formatDate = (value, forceEnglish = isEnglish.value) => {
  return forceEnglish
    ? formatEnglishGregorianDate(value)
    : formatJapaneseGregorianDate(value);
};

const currency = value =>
  `${isEnglish.value ? '¥' : '￥'}${Math.round(Number(value || 0))
    .toLocaleString(isEnglish.value ? 'en-US' : 'ja-JP')}`;
const number = value =>
  Math.round(Number(value || 0)).toLocaleString(isEnglish.value ? 'en-US' : 'ja-JP');

const roman = value => {
  const unicodeRoman = ['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ', 'Ⅶ', 'Ⅷ', 'Ⅸ', 'Ⅹ', 'Ⅺ', 'Ⅻ'];
  if (unicodeRoman[value - 1]) return unicodeRoman[value - 1];
  const values = [
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I']
  ];
  let numberValue = Number(value || 0);
  return values.reduce((result, [amount, character]) => {
    while (numberValue >= amount) {
      result += character;
      numberValue -= amount;
    }
    return result;
  }, '');
};

const lineName = line => {
  if (line.line_type === 'discount') {
    return line.item_nm || label('値引', 'Discount');
  }
  if (line.line_type === 'note') {
    return line.item_nm || label('注記', 'Note');
  }
  return line.item_nm;
};

const linePart = line => line.part_nm;

const lineSpecification = line => line.specification;

const lineUnit = line => line.unit_nm;

const lineNotes = line => line.notes;

const lineText = line => [
  lineName(line),
  linePart(line),
  lineSpecification(line),
  lineNotes(line)
].filter(Boolean).join(' ');

const formatQuantity = (value, decimalPlaces) => {
  return formatEstimateQuantity(
    value,
    isEnglish.value ? 'en-US' : 'ja-JP',
    decimalPlaces
  );
};

const unitPrice = line => {
  if (['note', 'discount'].includes(line.line_type) || line.unit_price == null) return '';
  return number(line.unit_price);
};

const lineAmount = line => {
  if (line.line_type === 'note') return '';
  const value = number(line.amount);
  return line.line_type === 'discount' ? `-${value}` : value;
};
</script>

<style scoped>
.detailed-pdf-document {
  --pdf-rule: 1px solid #666;
  --pdf-rule-light: 1px solid #a9b0b9;
  width: 297mm;
  color: #111;
  background: #fff;
  font-family: "Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", "MS Mincho", serif;
  font-size: 9pt;
  line-height: 1.35;
}

.detailed-pdf-document.english-document {
  font-family: "Times New Roman", Times, serif;
}

.detailed-pdf-page {
  position: relative;
  box-sizing: border-box;
  width: 297mm;
  height: 210mm;
  padding: 10mm 12mm 9mm;
  overflow: hidden;
  background: #fff;
}

.cover-page {
  padding: 10mm 14mm 9mm;
}

.page-break {
  break-before: page;
  page-break-before: always;
}

.draft-watermark {
  position: absolute;
  top: 78mm;
  left: 67mm;
  z-index: 0;
  color: rgba(180, 0, 0, 0.07);
  font-family: Arial, sans-serif;
  font-size: 64pt;
  font-weight: 700;
  letter-spacing: 10mm;
  transform: rotate(-18deg);
}

.detailed-pdf-page > *:not(.draft-watermark) {
  position: relative;
  z-index: 1;
}

.recipient-block {
  min-height: 15mm;
}

.recipient-name {
  margin-bottom: 2mm;
  padding-bottom: 1mm;
  border-bottom: var(--pdf-rule);
  font-size: 15pt;
}

.cover-title {
  margin: 0 auto 7mm;
  padding-left: 5mm;
  font-size: 23pt;
  font-weight: 600;
  letter-spacing: 5mm;
  text-align: center;
}

.english-document .cover-title {
  letter-spacing: 2mm;
}

.cover-main {
  display: grid;
  min-height: 67mm;
  grid-template-columns: minmax(0, 1.6fr) minmax(70mm, 0.8fr);
  align-items: start;
  gap: 16mm;
}

.cover-issuer-area {
  font-size: 9pt;
  line-height: 1.7;
  text-align: left;
}

.cover-estimate-number span {
  margin-left: 4mm;
}

.cover-company-block {
  position: relative;
  box-sizing: border-box;
  margin-top: 3mm;
}

.cover-company-block--with-seal {
  padding-top: 17.5mm;
}

.cover-issuer-area .company-block {
  text-align: left;
}

.company-seal {
  position: absolute;
  top: 0;
  right: 16mm;
}

.grand-total {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  margin: 1mm 0 5mm;
  padding: 3mm 5mm;
  border: var(--pdf-rule);
  align-items: baseline;
  gap: 5mm;
  font-size: 12pt;
}

.grand-total strong {
  flex: 1;
  font-size: 22pt;
  font-weight: 400;
  text-align: left;
}

.grand-total small {
  margin-left: auto;
  color: #555;
  font-size: 9pt;
}

.cover-amount-table {
  width: 120mm;
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
  border-collapse: separate;
  border-spacing: 0;
  font-size: 10.5pt;
}

.cover-amount-table th,
.cover-amount-table td {
  box-sizing: border-box;
  padding: 1.5mm 3mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
}

.cover-amount-table th {
  font-weight: 400;
  text-align: left;
}

.cover-amount-table td {
  text-align: right;
}

.cover-amount-total-row {
  font-weight: 400;
}

.cover-condition-table {
  width: 100%;
  margin-top: 4mm;
  border-top: var(--pdf-rule-light);
  border-left: var(--pdf-rule-light);
  border-collapse: separate;
  border-spacing: 0;
  font-size: 9.5pt;
}

.cover-condition-table th,
.cover-condition-table td {
  padding: 2mm 3mm;
  border: 0;
  border-right: var(--pdf-rule-light);
  border-bottom: var(--pdf-rule-light);
}

.cover-condition-table th {
  width: 27mm;
  background: rgba(0, 0, 0, 0.03);
  font-weight: 400;
}

.cover-notes {
  margin-top: 5mm;
  font-size: 9pt;
  white-space: pre-line;
}

.cover-notes > div + div {
  margin-top: 1mm;
}

.company-block {
  text-align: center;
}

.company-block :deep(.company-name) {
  font-size: 14pt;
  font-weight: 700;
  letter-spacing: 1mm;
}

.cover-approval-stamp {
  display: grid;
  margin-top: 2mm;
  margin-left: auto;
  width: 52.5mm;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 5mm 17.5mm;
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
  font-size: 6pt;
}

.cover-approval-stamp > span {
  display: flex;
  box-sizing: border-box;
  padding: 0.5mm;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  align-items: center;
  justify-content: center;
  font-weight: 400;
  text-align: center;
}

.cover-approval-stamp > span:nth-child(n + 4) {
  position: relative;
}

.cover-approval-stamp .approval-employee-seal {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 36px;
  height: 36px;
  margin: auto;
}

.draft-note {
  color: #a00;
}

.page-header {
  display: block;
  min-height: 17mm;
}

.page-header :deep(h2) {
  margin: 0;
  font-size: 20pt;
  font-weight: 500;
  letter-spacing: 3mm;
  text-align: center;
}

.english-document .page-header :deep(h2) {
  letter-spacing: 1mm;
}

.page-footer {
  position: absolute !important;
  right: 12mm;
  bottom: 4mm;
  left: 12mm;
  font-size: 8pt;
  text-align: center;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.breakdown-table,
.detail-table {
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
}

.breakdown-table th,
.breakdown-table td {
  height: 7.5mm;
  padding: 0.9mm 2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.breakdown-table th {
  background: #fff;
  font-size: 10pt;
  font-weight: 400;
  text-align: center;
}

.breakdown-no {
  width: 16mm;
}

.breakdown-quantity {
  width: 18mm;
}

.breakdown-unit {
  width: 18mm;
}

.breakdown-amount {
  width: 37mm;
}

.breakdown-notes {
  width: 40mm;
}

.breakdown-total-row {
  font-size: 11pt;
  font-weight: 400;
  text-align: right;
}

.breakdown-tax-row {
  font-size: 10pt;
  text-align: right;
}

.breakdown-note-row td {
  padding-left: 3mm;
  font-size: 8.5pt;
}

.detail-table th,
.detail-table td {
  height: 6mm;
  padding: 0.4mm 1.2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  vertical-align: middle;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.detail-table th {
  height: 8mm;
  background: #fff;
  font-weight: 400;
  text-align: center;
}

.detail-name {
  width: 58mm;
}

.detail-part {
  width: 39mm;
}

.detail-spec {
  width: 59mm;
}

.detail-quantity {
  width: 16mm;
}

.detail-unit {
  width: 15mm;
}

.detail-price,
.detail-amount {
  width: 25mm;
}

.detail-notes {
  width: 36mm;
}

.detail-section-row {
  font-size: 11pt;
  font-weight: 400;
}

.detail-section-row td {
  background: #fff;
}

.detail-subtotal-row,
.detail-grand-total-row {
  font-size: 10pt;
  font-weight: 400;
  text-align: center;
}

.detail-subtotal-row {
  text-align: right;
}

.detail-grand-total-row td {
  background: #fff;
}

.number-cell {
  white-space: nowrap;
  text-align: right;
}

.center-cell,
.empty-cell {
  text-align: center;
}

.multiline {
  white-space: pre-wrap;
}
</style>
